// One Scout chat thread as the browser sees it, and how each live-feed event changes it (GRO-274).
//
// The feed is `GET /api/chat/stream` (server-sent events) or its long-poll fallback; both carry
// the events Scout's projection sends (`scout/agent/lib/turn_progress.ts`). Pure, so
// `chatFeed.test.ts` runs it under `node --test`. The store (`chatStore.ts`) owns the threads.

import type { ChatLine } from './chatThread.ts';

/** One event on the feed. `index` is its absolute place on the thread's stream: the cursor. */
export interface FeedEvent {
  type: string;
  index?: number;
  turn_id?: string;
  delta?: string;
  text?: string;
  final?: boolean;
  call_id?: string;
  tool?: string;
  label?: string;
  ok?: boolean;
  outcome?: string;
  error?: string | null;
  message?: string;
  cursor?: number;
}

/** A tool step shown under the turn while it runs: "Searching news for Ema…". */
export interface LiveTool {
  call_id: string;
  label: string;
  done: boolean;
  ok: boolean;
}

export interface ThreadView {
  lines: ChatLine[];
  // The turn on screen while it runs: text so far, and its tool steps.
  liveTurnId: string | null;
  liveText: string;
  liveTools: LiveTool[];
  // A send is in flight or a turn is running; `awaitingTurns` counts sends whose turn has not ended.
  running: boolean;
  awaitingTurns: number;
  // Where to re-attach on the stream. Null until the first send or history read names one.
  cursor: number | null;
  error: string | null;
  // The turn parked on an Approve card: the panel reads the card, the feed stops.
  waitingInput: boolean;
  historyLoaded: boolean;
}

export const EMPTY_THREAD: ThreadView = {
  lines: [], liveTurnId: null, liveText: '', liveTools: [], running: false, awaitingTurns: 0,
  cursor: null, error: null, waitingInput: false, historyLoaded: false,
};

// What a stopped or failed turn says when it streamed nothing.
const STOPPED_NOTE = 'Stopped.';
const FAILED_NOTE = 'Scout stopped on an error.';
const RETRY_HINT = 'Send it again to retry.';

/** The feed events after which nothing more comes until the next message. */
export function endsFeed(event: FeedEvent): boolean {
  return event.type === 'idle' || event.type === 'session_ended' || event.type === 'input_requested';
}

/** How one long-poll read ended: a parked card or an ended session ends it, as on the stream. */
export function pollReadOutcome(events: FeedEvent[], replyEnded: boolean): { ended: boolean; failed: boolean } {
  const failed = events.some(event => event.type === 'relay_error');
  const ended = replyEnded || events.some(endsFeed);
  return { ended: ended && !failed, failed };
}

/** The line a live turn leaves in the transcript when it ends: its text, else a note. */
function lineForEndedTurn(view: ThreadView, outcome: string, error: string | null | undefined): ChatLine | null {
  const text = view.liveText.trim();
  if (outcome === 'completed') return text ? { role: 'assistant', text: view.liveText } : null;
  if (outcome === 'cancelled') return { role: 'assistant', text: text ? `${view.liveText}\n\n_${STOPPED_NOTE}_` : `_${STOPPED_NOTE}_` };
  const reason = error ? `${FAILED_NOTE} ${error} ${RETRY_HINT}` : `${FAILED_NOTE} ${RETRY_HINT}`;
  return { role: 'assistant', text: text ? `${view.liveText}\n\n_${reason}_` : `_${reason}_` };
}

/** A new live turn, or the same one replayed from its start after a reconnect. */
function startedTurn(view: ThreadView, turnId: string): ThreadView {
  return { ...view, liveTurnId: turnId, liveText: '', liveTools: [], running: true, waitingInput: false, error: null };
}

/** The tool list with one step started, or replaced when a replay sends it again. */
function withToolStarted(tools: LiveTool[], event: FeedEvent): LiveTool[] {
  const callId = event.call_id || `${event.tool}-${event.index}`;
  const others = tools.filter(tool => tool.call_id !== callId);
  return [...others, { call_id: callId, label: event.label || 'Working…', done: false, ok: true }];
}

/** The tool list with one step marked done. */
function withToolFinished(tools: LiveTool[], event: FeedEvent): LiveTool[] {
  return tools.map(tool => (tool.call_id === event.call_id ? { ...tool, done: true, ok: event.ok !== false } : tool));
}

/** The thread after one feed event. The cursor moves past every event that has an index. */
export function applyFeedEvent(view: ThreadView, event: FeedEvent): ThreadView {
  const cursor = typeof event.index === 'number' ? event.index + 1 : view.cursor;
  const next = { ...view, cursor };
  const turnId = event.turn_id || '';

  switch (event.type) {
    case 'turn_started':
      return { ...startedTurn(next, turnId), cursor };
    case 'text_delta':
      return { ...next, running: true, liveTurnId: turnId || next.liveTurnId, liveText: next.liveText + (event.delta || '') };

    // A step's text is final as written: narration before a tool, or the answer itself.
    case 'message_done': {
      const text = event.text || '';
      if (!text) return next;
      if (event.final) return { ...next, liveText: text };
      return { ...next, liveText: text + '\n\n' };
    }
    case 'tool_started':
      return { ...next, running: true, liveTools: withToolStarted(next.liveTools, event) };
    case 'tool_finished':
      return { ...next, liveTools: withToolFinished(next.liveTools, event) };

    // The turn parked on an Approve card: what it said so far stays, the card comes from /pending.
    case 'input_requested': {
      const text = next.liveText.trim();
      const lines = text ? [...next.lines, { role: 'assistant' as const, text: next.liveText.trim() }] : next.lines;
      return { ...next, lines, liveTurnId: null, liveText: '', liveTools: [], running: false, waitingInput: true };
    }
    case 'turn_ended': {
      const line = lineForEndedTurn(next, event.outcome || 'completed', event.error);
      const awaitingTurns = Math.max(0, next.awaitingTurns - 1);
      return {
        ...next, lines: line ? [...next.lines, line] : next.lines, liveTurnId: null, liveText: '', liveTools: [],
        awaitingTurns, running: awaitingTurns > 0,
      };
    }
    case 'idle':
      return { ...next, running: next.awaitingTurns > 0 };
    case 'session_ended':
      return {
        ...next, running: false, awaitingTurns: 0, liveTurnId: null, liveText: '', liveTools: [],
        error: event.outcome === 'failed' ? 'This conversation has ended. Start a new chat.' : next.error,
      };
    case 'relay_error':
      return { ...next, error: event.message || 'The live feed failed.' };
    default:
      return next;
  }
}

/** Server-sent events out of a text buffer: the complete frames' data, and the unfinished rest. */
export function parseSseBuffer(buffer: string): { events: FeedEvent[]; rest: string } {
  const events: FeedEvent[] = [];
  const normalised = buffer.replace(/\r\n/g, '\n');
  const frames = normalised.split('\n\n');
  const rest = frames.pop() ?? '';
  for (const frame of frames) {
    const dataLines = frame.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart());
    if (dataLines.length === 0) continue;
    try {
      const parsed: unknown = JSON.parse(dataLines.join('\n'));
      if (parsed && typeof parsed === 'object' && typeof (parsed as FeedEvent).type === 'string') events.push(parsed as FeedEvent);
    } catch {
      // A frame that is not JSON is not ours; skip it rather than drop the stream.
    }
  }
  return { events, rest };
}

/** The composer's key rule: Enter sends, Shift+Enter is a new line, and an IME composition never sends. */
export function sendsOnKey(key: string, shiftKey: boolean, isComposing: boolean): boolean {
  return key === 'Enter' && !shiftKey && !isComposing;
}

/** The status line under a running turn when no tool is mid-flight. */
export function workingLabel(view: ThreadView): string {
  const running = view.liveTools.filter(tool => !tool.done);
  if (running.length > 0) return running[running.length - 1]!.label;
  if (view.liveText) return 'Writing…';
  return 'Scout is working…';
}
